import { z } from "zod";

/** นโยบาย PDPA/ความเป็นส่วนตัวที่ Admin แก้ได้: ข้อความไทยและอังกฤษ เขียนด้วยรูปแบบง่าย (## หัวข้อ, - รายการ, ย่อหน้าคั่นด้วยบรรทัดว่าง) */
export const POLICY_MAX_CHARS = 60000;

export const policyDocSchema = z.object({
  th: z.string().trim().min(20).max(POLICY_MAX_CHARS),
  en: z.string().trim().min(20).max(POLICY_MAX_CHARS),
  /** true = ออกเวอร์ชันใหม่ ผู้ใช้ทุกคนต้องยินยอมใหม่ก่อนใช้ AI/อัปโหลด */
  requireReconsent: z.boolean(),
  note: z.string().trim().max(200).optional(),
});
export type PolicyDoc = z.infer<typeof policyDocSchema>;

/** นโยบายแต่ละฉบับที่ Admin แก้ได้ privacy เป็นฉบับเดียวที่ผูกกับความยินยอม (PDPA) ส่วนที่เหลือแสดงแทนข้อความตั้งต้นในหน้า /policies */
export const POLICY_IDS = ["privacy", "cookies", "terms", "business", "refund"] as const;
export type PolicyId = (typeof POLICY_IDS)[number];
export const policyIdParam = z.enum(POLICY_IDS);

/** GET /policy (สาธารณะ): version คือเวอร์ชันที่ผู้ใช้ต้องยินยอม (ของ privacy) docs คือรายฉบับ */
export interface PolicyBundle {
  version: string;
  docs: Record<PolicyId, PolicyDto>;
}

/** นโยบายหนึ่งฉบับ */
export interface PolicyDto {
  /** เวอร์ชันที่ผู้ใช้ต้องยินยอม (ถ้ายังไม่มีฉบับที่ Admin แก้ ใช้ CURRENT_CONSENT_VERSION) */
  version: string;
  /** true = มีฉบับที่ Admin แก้ (แสดงแทนข้อความตั้งต้นในแอป) */
  custom: boolean;
  updatedAt: string | null;
  th?: string;
  en?: string;
}

export interface PolicyRevisionMeta {
  rev: string;
  version: string;
  requireReconsent: boolean;
  note?: string;
  by?: string;
  createdAt: string;
  reset?: boolean;
}

/** รหัสเวอร์ชันใหม่จากเวลา เช่น 20261013-0530 (UTC) */
export function newPolicyVersion(now: Date = new Date()): string {
  return now.toISOString().slice(0, 16).replace(/[-:]/g, "").replace("T", "-");
}

export type PolicyBlock = { type: "h"; text: string } | { type: "p"; text: string } | { type: "ul"; items: string[] };

/** แปลงข้อความเป็นบล็อก: "## " หัวข้อ, "- " หรือ "* " รายการ, บรรทัดต่อเนื่องรวมเป็นย่อหน้าเดียว ไม่รองรับ HTML (แสดงเป็นข้อความธรรมดา) */
export function parsePolicyText(text: string): PolicyBlock[] {
  const out: PolicyBlock[] = [];
  let para: string[] = [];
  let list: string[] | null = null;
  const flushPara = () => {
    if (para.length) out.push({ type: "p", text: para.join(" ") });
    para = [];
  };
  const flushList = () => {
    if (list?.length) out.push({ type: "ul", items: list });
    list = null;
  };
  for (const raw of text.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
    } else if (/^#{1,6}\s+/.test(line)) {
      flushPara();
      flushList();
      out.push({ type: "h", text: line.replace(/^#{1,6}\s+/, "") });
    } else if (/^[-*]\s+/.test(line)) {
      flushPara();
      (list ??= []).push(line.replace(/^[-*]\s+/, ""));
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return out;
}
